import { useState } from 'react';
import { ModelResults } from '../domain/types';
import { FiDollarSign, FiActivity, FiFileText, FiInfo } from 'react-icons/fi';
import { toSigFigs } from '../../../utils/format';
import { useImpactMode } from '../../impact/context/ImpactModeContext';
import { useRangeMode } from '../../impact/context/RangeModeContext';
import { RangeBar } from '../../impact/components/RangeVisuals';
import { isRangeReady, midpointRanges, type ScenariosBlock, type MidpointSection } from '../../impact/domain/scenarios';
import { MIDPOINT_DEFINITIONS, ENDPOINT_DEFINITIONS, getCategoryDisplayName, isHiddenCategory } from '../domain/definitions';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChartData } from 'chart.js';
import { LifetimeProjection } from '../../lifetime/domain/types';

/** Find the best matching midpoint definition for a category name */
function findMidpointDef(category: string): string | undefined {
    if (MIDPOINT_DEFINITIONS[category]) return MIDPOINT_DEFINITIONS[category];
    const lc = category.toLowerCase();
    for (const [key, def] of Object.entries(MIDPOINT_DEFINITIONS)) {
        if (key.toLowerCase() === lc) return def;
    }
    for (const [key, def] of Object.entries(MIDPOINT_DEFINITIONS)) {
        if (lc.includes(key.toLowerCase()) || key.toLowerCase().includes(lc)) return def;
    }
    return undefined;
}

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type TimeFrame = 'year1' | 'lifetime';

interface ImpactMetricsProps {
    modelResults: ModelResults;
    projectName: string;
    analysisType?: 'Operational_Analysis' | 'Cradle_to_Grave_Analysis';
    year?: number;
    isExistingProject?: boolean;
    lifetimeProjection?: LifetimeProjection | null;
    scenarios?: ScenariosBlock;
}

type MetricTab = 'economic' | 'dalys' | 'pdfs';

// Define more specific types for our different data structures
interface ImpactEntry {
    Impact_Category: string;
    Total_Impact?: number;
    USD: number;
}

interface AnalysisData {
    Total?: {
        USD: number;
        PDFs: number;
        DALYs: number;
    };
    Public_Health?: ImpactEntry[];
    Biodiversity?: ImpactEntry[];
    Economic?: ImpactEntry[];
    [key: string]: any; // For dynamic indexing
}

export function ImpactMetrics({
    modelResults,
    projectName,
    analysisType: analysisTypeProp,
    year,
    isExistingProject,
    lifetimeProjection,
    scenarios
}: ImpactMetricsProps) {
    const [activeTab, setActiveTab] = useState<MetricTab>('economic');
    const [timeFrame, setTimeFrame] = useState<TimeFrame>('year1');
    const { mode } = useImpactMode();
    const { mode: rangeMode } = useRangeMode();
    const showRange = rangeMode === 'range' && isRangeReady(scenarios);

    // Use context mode if no explicit prop override.
    // Operational_Analysis = ops-only (cradle-to-grave minus infrastructure).
    // Cradle_to_Grave_Analysis = full cradle-to-grave (with infrastructure).
    const analysisType = analysisTypeProp ?? (mode === 'ops' ? 'Operational_Analysis' : 'Cradle_to_Grave_Analysis');

    const hasLifetime = !!lifetimeProjection;

    // Determine if this is a future project based on run_type or prop
    const isFutureProject = isExistingProject === false || modelResults.run_type === 'future';

    // Extract year from the model results if not provided as prop
    const impactYear = year || modelResults.year || new Date().getFullYear();

    // Generate appropriate title based on project type and time frame
    const getImpactTitle = () => {
        if (timeFrame === 'lifetime' && lifetimeProjection) {
            const years = lifetimeProjection.config.projectLifetimeYears;
            return `${years}-Year Lifetime Explorer`;
        }
        if (isFutureProject) {
            return 'Prospective Impact Explorer';
        }
        return `${impactYear} Impact Explorer`;
    };

    // Extract metric values from model results using the correct path
    const impacts = modelResults.summary_json?.Impacts || {};
    const analysisData = (impacts[analysisType] || {}) as AnalysisData;

    // Get total values for quick metrics display
    const totalValues = analysisData.Total || { USD: 0, PDFs: 0, DALYs: 0 };

    // Get detailed breakdowns for more comprehensive visualizations
    const publicHealthData = analysisData.Public_Health || [];
    const biodiversityData = analysisData.Biodiversity || [];

    // Combine economic impacts from all sections (similar to Python script)
    const combinedEconomicData: ImpactEntry[] = [];
    const sectionKeys = ['Public_Health', 'Biodiversity', 'Economic'] as const;

    sectionKeys.forEach(section => {
        if (analysisData[section]) {
            (analysisData[section] as ImpactEntry[]).forEach(entry => {
                if (entry.USD) {
                    combinedEconomicData.push({
                        Impact_Category: entry.Impact_Category || 'Unknown',
                        USD: entry.USD,
                        Total_Impact: entry.Total_Impact
                    });
                }
            });
        }
    });

    // Group by category and filter out small values (similar to Python script)
    const economicByCategory: Record<string, number> = {};
    let totalEconomicAbs = 0;
    combinedEconomicData.forEach(item => {
        if (!economicByCategory[item.Impact_Category]) {
            economicByCategory[item.Impact_Category] = 0;
        }
        economicByCategory[item.Impact_Category] += item.USD;
        totalEconomicAbs += Math.abs(item.USD);
    });

    // Convert to array and filter out small impacts (< 1% of total magnitude)
    const significantEconomicData = Object.entries(economicByCategory)
        .map(([category, value]) => ({
            Impact_Category: category,
            USD: value,
            Percentage: totalEconomicAbs !== 0 ? (Math.abs(value) / totalEconomicAbs) * 100 : 0,
            Total_Impact: value // Add Total_Impact property for consistency
        }))
        .filter(item => item.Percentage > 1.0)
        .sort((a, b) => Math.abs(b.USD) - Math.abs(a.USD)); // Sort by magnitude

    // Detect if any per-category values are negative (net burden subcategories)
    const hasNegativeBars = [...publicHealthData, ...biodiversityData, ...combinedEconomicData]
        .some(d => (d.Total_Impact ?? d.USD ?? 0) < 0);

    // Determine displayed values based on time frame
    const isLifetime = timeFrame === 'lifetime' && lifetimeProjection;

    const displayValues = {
        USD: isLifetime ? lifetimeProjection!.lifetimeEconomicNpvUsd : totalValues.USD,
        DALYs: isLifetime ? lifetimeProjection!.lifetimeAvoidedDalys : totalValues.DALYs,
        PDFs: isLifetime ? lifetimeProjection!.lifetimeAvoidedPdfs : totalValues.PDFs,
    };

    const metrics = {
        economic: {
            value: displayValues.USD,
            unit: '$',
            label: 'Community Economic Benefits',
            description: isLifetime
                ? 'Net present value of avoided impacts over project lifetime'
                : 'Monetized value of avoided health impacts and environmental damage',
            detailedData: significantEconomicData
        },
        dalys: {
            value: displayValues.DALYs,
            unit: '',
            label: 'Years of Healthy Life Saved',
            description: isLifetime
                ? 'Cumulative health benefits over project lifetime'
                : 'Health benefits from reduced air pollution and environmental damage',
            detailedData: publicHealthData.sort((a, b) => (b.Total_Impact || 0) - (a.Total_Impact || 0))
        },
        pdfs: {
            value: displayValues.PDFs,
            unit: '',
            label: 'PDF Species Protected',
            description: isLifetime
                ? 'Cumulative biodiversity benefits over project lifetime'
                : 'Biodiversity preserved through reduced ecosystem damage',
            detailedData: biodiversityData.sort((a, b) => (b.Total_Impact || 0) - (a.Total_Impact || 0))
        }
    };

    // For lifetime mode, compute a scaling factor from year-1 total to lifetime total
    const getLifetimeScale = (tab: MetricTab): number => {
        if (!isLifetime || !lifetimeProjection) return 1;
        const y1 = tab === 'economic' ? totalValues.USD
            : tab === 'dalys' ? totalValues.DALYs
            : totalValues.PDFs;
        const lt = tab === 'economic' ? lifetimeProjection.lifetimeEconomicNpvUsd
            : tab === 'dalys' ? lifetimeProjection.lifetimeAvoidedDalys
            : lifetimeProjection.lifetimeAvoidedPdfs;
        return y1 !== 0 ? lt / y1 : 1;
    };

    // Prepare chart data based on active tab
    const getChartData = (): ChartData<'bar'> => {
        const currentMetric = metrics[activeTab];
        let labels: string[] = [];
        let values: number[] = [];
        let dataField = '';
        const scale = getLifetimeScale(activeTab);

        // Model already applies avoided framing (positive = benefit), no negation needed.
        // Drop hidden categories (e.g. "Land Use")
        // before deriving labels/values so the chart matches the emissions tables.
        const visible = currentMetric.detailedData.filter(d => !isHiddenCategory(d.Impact_Category));
        if (activeTab === 'economic') {
            labels = visible.map(d => getCategoryDisplayName(d.Impact_Category));
            values = visible.map(d => (d.USD * scale) / 1000000);
            dataField = isLifetime ? 'Lifetime Economic Benefit (Million $)' : 'Economic Benefit (Million $)';
        } else if (activeTab === 'dalys') {
            labels = visible.map(d => getCategoryDisplayName(d.Impact_Category));
            values = visible.map(d => (d.Total_Impact || 0) * scale);
            dataField = isLifetime ? 'Lifetime DALYs' : 'Disability Adjusted Life Years (DALY)';
        } else {
            labels = visible.map(d => getCategoryDisplayName(d.Impact_Category));
            values = visible.map(d => (d.Total_Impact || 0) * scale);
            dataField = isLifetime ? 'Lifetime PDFs' : 'Potentially Disappeared Fraction (PDF)';
        }

        // Dark mode colors matching the design system
        const positiveColor = activeTab === 'economic' ? '#22C55E' : // economic-400 (green)
                              activeTab === 'dalys' ? '#FBBF24' :    // health-400 (yellow/amber)
                              '#3B82F6';                              // ecosystem-400 (blue)
        const negativeColor = '#EF4444'; // red for infrastructure burdens

        // Per-bar coloring: positive = category color, negative = red
        const barColors = values.map(v => v >= 0 ? positiveColor : negativeColor);

        return {
            labels,
            datasets: [
                {
                    label: dataField,
                    data: values,
                    backgroundColor: barColors,
                    barThickness: 'flex' as const,
                    barPercentage: 0.8,
                }
            ]
        };
    };

    // Placeholder chart with real ReCiPe subcategory names so users
    // can see what breakdown is available before running the model
    const PLACEHOLDER_LABELS: Record<MetricTab, string[]> = {
        economic: [
            'Climate Change',
            'Particulate Matter',
            'Fossil Resource Scarcity',
            'Human Toxicity',
            'Terrestrial Acidification',
            'Freshwater Eutrophication',
        ],
        dalys: [
            'Climate Change',
            'Particulate Matter',
            'Human Toxicity (Cancerous)',
            'Human Toxicity (Non-Cancerous)',
            'Water Use',
            'Ozone Formation',
        ],
        pdfs: [
            'Climate Change',
            'Terrestrial Acidification',
            'Freshwater Eutrophication',
            'Ozone Formation',
            'Land Use',
            'Freshwater Ecotoxicity',
        ],
    };
    const PLACEHOLDER_VALUES: Record<MetricTab, number[]> = {
        economic: [73, 32, 26, 19, 3.5, 2.1],
        dalys:    [410, 180, 59, 48, 1.6, 0.55],
        pdfs:     [1.2, 0.19, 0.12, 0.081, 0.026, 0.013],
    };

    const getPlaceholderChart = (): ChartData<'bar'> => {
        const barColor = activeTab === 'economic' ? '#22C55E' :
                         activeTab === 'dalys' ? '#FBBF24' : '#3B82F6';
        return {
            labels: PLACEHOLDER_LABELS[activeTab],
            datasets: [{
                label: 'Impact',
                data: PLACEHOLDER_VALUES[activeTab],
                backgroundColor: barColor,
                barThickness: 'flex' as const,
                barPercentage: 0.8,
            }],
        };
    };

    // Chart options with proper typing - dark mode styled
    const chartOptions: ChartOptions<'bar'> = {
        indexAxis: 'y' as const,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#1E293B', // surface-600
                titleColor: '#F8FAFC',      // text-primary
                bodyColor: '#CBD5E1',       // text-secondary
                borderColor: '#334155',     // border
                borderWidth: 1,
                titleFont: { size: 13, weight: 'bold' as const },
                bodyFont: { size: 12 },
                padding: 10,
                boxPadding: 4,
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.x !== null) {
                            if (activeTab === 'economic') {
                                label += `$${toSigFigs(context.parsed.x)}M`;
                            } else {
                                label += toSigFigs(context.parsed.x);
                            }
                        }
                        return label;
                    },
                    afterBody: function (tooltipItems) {
                        // label is the displayed (renamed) category; look up definition by both
                        const displayName = tooltipItems[0]?.label;
                        const def = displayName ? findMidpointDef(displayName) : undefined;
                        if (def) {
                            const words = def.split(' ');
                            const lines: string[] = [];
                            let line = '';
                            for (const w of words) {
                                if ((line + ' ' + w).length > 45) {
                                    lines.push(line.trim());
                                    line = w;
                                } else {
                                    line += ' ' + w;
                                }
                            }
                            if (line.trim()) lines.push(line.trim());
                            return ['', ...lines];
                        }
                        return [];
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    color: '#334155', // border color
                },
                ticks: {
                    color: '#94A3B8', // text-muted
                    callback: function (tickValue) {
                        const value = Number(tickValue);
                        if (activeTab === 'economic') {
                            return `$${toSigFigs(value)}M`;
                        } else {
                            return toSigFigs(value);
                        }
                    }
                }
            },
            y: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#CBD5E1', // text-secondary
                }
            }
        }
    };

    return (
        <div className="bg-surface-700 rounded-lg shadow border border-border mt-6 overflow-hidden">
            <div className="flex items-center justify-between p-6 pb-2">
                <h2 className="text-lg font-medium text-text-primary">
                    {getImpactTitle()}
                </h2>
                {hasLifetime && (
                    <div className="flex items-center bg-surface-600 rounded-md p-0.5">
                        <button
                            onClick={() => setTimeFrame('year1')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                timeFrame === 'year1'
                                    ? 'bg-surface-500 text-text-primary shadow-sm'
                                    : 'text-text-muted hover:text-text-secondary'
                            }`}
                        >
                            Year 1
                        </button>
                        <button
                            onClick={() => setTimeFrame('lifetime')}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                timeFrame === 'lifetime'
                                    ? 'bg-surface-500 text-text-primary shadow-sm'
                                    : 'text-text-muted hover:text-text-secondary'
                            }`}
                        >
                            Lifetime
                        </button>
                    </div>
                )}
            </div>

            {/* Description */}
            <div className="px-6 pt-1 pb-2">
                <p className="text-sm text-text-secondary">
                    {metrics[activeTab].description}
                </p>
            </div>

            {/* Chart */}
            <div className="px-6">
                <div className="h-80 w-full">
                    {metrics[activeTab].detailedData.length > 0 ? (
                        <Bar
                            data={getChartData()}
                            options={chartOptions}
                            height={300}
                        />
                    ) : (
                        <div className="relative h-full select-none">
                            <div className="blur-sm pointer-events-none">
                                <Bar
                                    data={getPlaceholderChart()}
                                    options={chartOptions}
                                    height={300}
                                />
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="bg-surface-600/90 text-text-secondary text-sm px-4 py-2 rounded-lg border border-border">
                                    Run the model to see detailed breakdown
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                {hasNegativeBars && metrics[activeTab].detailedData.length > 0 && (
                    <p className="text-[11px] text-text-muted mt-1 mb-0">
                        Colored bars = avoided impact &middot; Red bars = net burden from infrastructure or displaced clean sources
                    </p>
                )}
            </div>

            {/* Sensitivity range strip — Floor / Central / Ceiling per midpoint */}
            {showRange && (() => {
                const section: MidpointSection =
                    activeTab === 'economic' ? 'Economic'
                    : activeTab === 'dalys' ? 'Public_Health'
                    : 'Biodiversity';
                const color = activeTab === 'economic' ? '#22C55E'
                    : activeTab === 'dalys' ? '#FBBF24' : '#3B82F6';
                const rows = midpointRanges(scenarios, section)
                    .filter((r) => !isHiddenCategory(r.category));
                if (rows.length === 0) return null;
                const max = Math.max(0, ...rows.map((r) => Math.abs(r.ceiling)));
                const fmt = activeTab === 'economic'
                    ? (n: number) => `$${toSigFigs(n / 1_000_000)}M`
                    : (n: number) => toSigFigs(n);
                return (
                    <div className="px-6 mt-4">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1">
                            Sensitivity range · Floor–Ceiling
                        </p>
                        <div>
                            {rows.map((r) => (
                                <RangeBar
                                    key={r.category}
                                    label={getCategoryDisplayName(r.category)}
                                    floor={r.floor}
                                    central={r.central}
                                    ceiling={r.ceiling}
                                    max={max}
                                    color={color}
                                    format={fmt}
                                />
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* Tabs */}
            <div className="flex border-t border-border mt-4">
                <button
                    onClick={() => setActiveTab('economic')}
                    title={ENDPOINT_DEFINITIONS.economic.description}
                    className={`flex items-center justify-center gap-2 flex-1 py-3 text-sm font-medium transition-colors
            ${activeTab === 'economic' ? 'text-economic-400 border-t-2 border-economic-400' : 'text-text-muted hover:text-economic-400'}`}
                >
                    <FiDollarSign />
                    Economic
                </button>
                <button
                    onClick={() => setActiveTab('dalys')}
                    title={ENDPOINT_DEFINITIONS.health.description}
                    className={`flex items-center justify-center gap-2 flex-1 py-3 text-sm font-medium transition-colors
            ${activeTab === 'dalys' ? 'text-health-400 border-t-2 border-health-400' : 'text-text-muted hover:text-health-400'}`}
                >
                    <FiActivity />
                    Health
                </button>
                <button
                    onClick={() => setActiveTab('pdfs')}
                    title={ENDPOINT_DEFINITIONS.ecosystem.description}
                    className={`flex items-center justify-center gap-2 flex-1 py-3 text-sm font-medium transition-colors
            ${activeTab === 'pdfs' ? 'text-ecosystem-400 border-t-2 border-ecosystem-400' : 'text-text-muted hover:text-ecosystem-400'}`}
                >
                    <FiFileText />
                    Biodiversity
                </button>
            </div>
        </div>
    );
}